import React, { useState, useEffect, useContext } from 'react';
import { Container, Typography, Box, Tabs, Tab, Card, CardMedia, Modal, Button, IconButton } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import CloseIcon from '@mui/icons-material/Close';
import { motion, useAnimation } from 'framer-motion';
import '../styles/css/Portfolio.css';
import { LanguageContext } from '../App';

// Helper function to dynamically import images
const importAll = (r) => {
  return r.keys().map((item) => ({
    src: r(item),
    category: item.split('/')[1], // Get category from folder name
    title: item.split('/').pop().split('.')[0].replace(/_/g, ' '), // Use filename as title
  }));
};

// Use require.context to get all images
const getPortfolioItems = () => {
  try {
    // Adjust this path to match your actual structure
    const images = importAll(require.context('../styles/images/portfolio', true, /\.(png|jpe?g|webp)$/));
    return images.map((item, index) => ({
      id: index + 1,
      category: item.category,
      image: item.src,
      title: item.title,
    }));
  } catch (error) {
    console.error("Error loading images:", error);
    return [];
  }
};

const Portfolio = () => {
  const [currentTab, setCurrentTab] = useState('all');
  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [allItems, setAllItems] = useState([]);
  const [visibleItems, setVisibleItems] = useState([]);
  const [visibleCount, setVisibleCount] = useState(30); // Show 30 images per page
  const [filteredItems, setFilteredItems] = useState([]);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const controls = useAnimation();
  const { language, translations } = useContext(LanguageContext);

  useEffect(() => {
    // Load and shuffle all items
    const items = getPortfolioItems();
    const shuffled = [...items].sort(() => 0.5 - Math.random());
    setAllItems(shuffled);
  }, []);

  useEffect(() => {
    // Filter items based on current tab
    const filtered = currentTab === 'all' 
      ? allItems 
      : allItems.filter(item => item.category === currentTab);
    
    setFilteredItems(filtered);
    setVisibleItems(filtered.slice(0, visibleCount));
  }, [currentTab, allItems, visibleCount]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setVisibleCount(30); // Reset to 30 when changing tabs
  };

  const handleOpenModal = (image, index) => {
    setSelectedImage(image);
    setSelectedIndex(index);
    setOpenModal(true);
    
    // Reset animation controls when opening modal
    controls.start({
      opacity: 1,
      x: 0,
      transition: { duration: 0.3 }
    });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleLoadMore = () => {
    setVisibleCount(prevCount => prevCount + 30); // Load 30 more each time
  };

  const handleNext = () => {
    const newIndex = (selectedIndex + 1) % filteredItems.length;
    setSelectedIndex(newIndex);
    setSelectedImage(filteredItems[newIndex].image);
    // Animate from right to left (next)
    controls.start({
      x: [-50, 0],
      opacity: [0, 1],
      transition: { duration: 0.3 }
    });
  };

  const handlePrev = () => {
    const newIndex = (selectedIndex - 1 + filteredItems.length) % filteredItems.length;
    setSelectedIndex(newIndex);
    setSelectedImage(filteredItems[newIndex].image);
    // Animate from left to right (previous)
    controls.start({
      x: [50, 0],
      opacity: [0, 1],
      transition: { duration: 0.3 }
    });
  };

  // Handle swipe gestures
  const handleDragEnd = (event, info) => {
    if (info.offset.x > 100) {
      setSwipeDirection('right');
      handlePrev();
    } else if (info.offset.x < -100) {
      setSwipeDirection('left');
      handleNext();
    }
  };

  // Get all unique category keys from items for dynamic tab generation
  const categories = [...new Set(allItems.map(item => item.category))];
  const hasMoreItems = visibleItems.length < filteredItems.length;

  // Animation variants for grid items
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <Container className="portfolio-container" maxWidth={false} sx={{ padding: 0 }}>
      <Typography variant="h2" component="h2" className="section-title">
        {translations[language].portfolioTitle}
      </Typography>
      <Typography variant="subtitle1" className="section-subtitle">
        {translations[language].portfolioSubtitle}
      </Typography>
      
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}           
          scrollButtons="auto"
          className="portfolio-tabs"
          variant="scrollable"
          allowScrollButtonsMobile
          ScrollButtonComponent={IconButton}
          TabIndicatorProps={{
            style: { display: 'none' }
          }}
          sx={{ 
            flexWrap: 'wrap',
            '& .MuiTabs-flexContainer': {
              flexWrap: 'wrap',
              justifyContent: 'center'
            }
          }}
        >
          <Tab 
            className={`category ${currentTab === 'all' ? 'active-tab' : ''}`} 
            label={language === 'he' ? 'הכל' : 'All'} 
            value="all" 
          />
          {categories.map(category => (
            <Tab 
              className={`category ${currentTab === category ? 'active-tab' : ''}`}
              key={category} 
              label={translations[language].portfolioCategories[category] || category}
              value={category} 
            />
          ))}
        </Tabs>
      </Box>
      
      {/* Replace Grid with Masonry */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="portfolio-masonry">
          {visibleItems.map((item, index) => (
            <motion.div variants={itemVariants} key={item.id} className="portfolio-masonry-item">
              <Card 
                className="portfolio-item" 
                onClick={() => handleOpenModal(item.image, filteredItems.findIndex(i => i.id === item.id))}
              >
                <CardMedia
                  component="img"
                  image={item.image}
                  alt={item.title}
                  className="portfolio-image"
                  // Remove height prop to allow natural aspect ratio
                />
                <Box className="portfolio-overlay">
                  {/* Removed photo name display */}
                </Box>
              </Card>
            </motion.div>
          ))}
        </div>
      </motion.div>
      
      {hasMoreItems && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleLoadMore}
            className="load-more-button"
            component={motion.button}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {translations[language].loadMoreBtn}
          </Button>
        </Box>
      )}
      
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        className="portfolio-modal"
      >
        <Box className="modal-content">
          <div className="modal-backdrop"></div>
          
          <IconButton 
            className="modal-close"
            onClick={handleCloseModal}
            sx={{ position: 'absolute', top: 10, right: 10, color: 'white', zIndex: 1200 }}
          >
            <CloseIcon />
          </IconButton>
          
          <IconButton 
            className="modal-prev hide-on-mobile"
            onClick={handlePrev}
            sx={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'white', zIndex: 1200 }}
          >
            <NavigateBeforeIcon fontSize="large" />
          </IconButton>
          
          <motion.img 
            src={selectedImage} 
            alt="Full size" 
            initial={{ opacity: 1, x: 0 }}
            animate={controls}
            transition={{ duration: 0.3 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.7}
            onDragEnd={handleDragEnd}
            className="modal-image"
          />
          
          <IconButton 
            className="modal-next hide-on-mobile"
            onClick={handleNext}
            sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'white', zIndex: 1200 }}
          >
            <NavigateNextIcon fontSize="large" />
          </IconButton>
        </Box>
      </Modal>
    </Container>
  );
}

export default Portfolio;
